import React, { useCallback } from 'react';
import Plus from '@uber/base-icons/plus';
import { useStyletron } from 'baseui';
import { Button, KIND as BUTTON_KIND, SIZE as BUTTON_SIZE } from 'baseui/button';
import { v4 as uuid } from 'uuid';
import { RELATIONSHIP_CREATOR_CONSTANTS } from '../../constants';
import type { DraftRelationshipsMapT, FrameFieldT, SelectOptionT, TemporalScopeT } from '../../types/relationship';
import { TargetEntry } from './target-entry';
type SelectLabelRendererT = ({
  option
}: {
  option: Readonly<{
    [key: string]: unknown;
  }>;
}) => React.ReactNode;
type TargetsListProps = {
  draftRelationships: DraftRelationshipsMapT;
  isEditing: boolean;
  availableTargets: SelectOptionT[];
  temporalScope?: TemporalScopeT;
  sameInterval: boolean;
  sharedFrameRange: {
    startFrame?: number;
    endFrame?: number;
  };
  validationErrors?: Record<string, {
    frameRange: {
      startError: string | undefined;
      endError: string | undefined;
    };
    error?: string;
  }>;
  activeTargetEntryId?: string;
  tooltipText?: string;
  onDraftRelationshipsChange: (drafts: DraftRelationshipsMapT) => void;
  onAddTarget?: (id: string) => void;
  onRemoveTarget?: (id: string) => void;
  onTargetEntryFocus?: (id: string) => void;
  handleTargetChange: (id: string, targetId: string) => void;
  handleTargetFrameChange: (id: string, rangeIndex: number, field: FrameFieldT, value: number | undefined) => void;
  noResultsMsg?: string;
  getTargetOptionLabel?: SelectLabelRendererT;
  getTargetValueLabel?: SelectLabelRendererT;
};
export const TargetsList = ({
  draftRelationships,
  isEditing,
  availableTargets,
  temporalScope,
  sameInterval,
  sharedFrameRange,
  validationErrors,
  activeTargetEntryId,
  tooltipText,
  onDraftRelationshipsChange,
  onAddTarget,
  onRemoveTarget,
  onTargetEntryFocus,
  handleTargetChange,
  handleTargetFrameChange,
  noResultsMsg = 'No results',
  getTargetOptionLabel,
  getTargetValueLabel
}: TargetsListProps) => {
  const [css, theme] = useStyletron();
  const entries = Object.values(draftRelationships);
  const showAddButton = !isEditing;
  const addDraftRelationship = useCallback(() => {
    const id = uuid();
    onDraftRelationshipsChange({
      ...draftRelationships,
      [id]: {
        id,
        targetId: '',
        frameRanges: [{}]
      }
    });
    onAddTarget?.(id);
  }, [draftRelationships, onDraftRelationshipsChange, onAddTarget]);
  const removeDraftRelationship = useCallback((id: string) => {
    const {
      [id]: _,
      ...rest
    } = draftRelationships;
    onDraftRelationshipsChange(rest);
    onRemoveTarget?.(id);
  }, [draftRelationships, onDraftRelationshipsChange, onRemoveTarget]);
  return <div className={css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.sizing.scale800
  })}>
      {entries.map((entry, index) => <React.Fragment key={entry.id}>
          {index > 0 && <hr className={css({
        width: '100%',
        height: '1px',
        border: 'none',
        backgroundColor: theme.colors.borderOpaque,
        margin: 0
      })} />}
          <TargetEntry index={index} entry={entry} availableTargets={availableTargets} temporalScope={temporalScope} sameInterval={sameInterval} sharedFrameRange={sharedFrameRange} showRemove={showAddButton && entries.length > 1} frameRangeErrors={validationErrors?.[entry.id]?.frameRange} error={validationErrors?.[entry.id]?.error} tooltipText={entry.id === activeTargetEntryId ? tooltipText : undefined} onTargetChange={handleTargetChange} onFrameChange={handleTargetFrameChange} onRemove={removeDraftRelationship} onFocus={onTargetEntryFocus} noResultsMsg={noResultsMsg} getOptionLabel={getTargetOptionLabel} getValueLabel={getTargetValueLabel} />
        </React.Fragment>)}

      {showAddButton && <Button kind={BUTTON_KIND.secondary} size={BUTTON_SIZE.compact} endEnhancer={<Plus size={12} />} onClick={addDraftRelationship}>
          {RELATIONSHIP_CREATOR_CONSTANTS.ADD_TARGET_BUTTON}
        </Button>}
    </div>;
};